<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import gettext from '@/i18n/gettext';
import type { CatalogueProject } from '@/models/Projects';
import { CatalogueProjectBomDuplicateType } from '@/models/Projects';
import { getProjects } from '@/api/ProjectsApi';

const { $gettext } = gettext;

const props = defineProps<{
  modelValue: boolean;
  title?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  confirm: [
    targetProjectUuid: string | null,
    branchType: CatalogueProjectBomDuplicateType,
  ];
}>();

const projects = ref<CatalogueProject[]>([]);
const loading = ref(false);
const selectedTargetProjectUuid = ref<string | null>(null);
const branchType = ref<CatalogueProjectBomDuplicateType>(
  CatalogueProjectBomDuplicateType.default,
);
const showProjectSelect = ref(true);

const close = () => emit('update:modelValue', false);

const ensureProjects = async () => {
  loading.value = true;
  try {
    projects.value = await getProjects();
  } catch (e) {
    console.error('Failed to load projects for duplicate dialog:', e);
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      selectedTargetProjectUuid.value = null;
      branchType.value = CatalogueProjectBomDuplicateType.default;
      showProjectSelect.value = true;
      await ensureProjects();
    }
  },
);

onMounted(async () => {
  if (props.modelValue) {
    await ensureProjects();
  }
});

const onConfirm = () => {
  const isDefault =
    branchType.value === CatalogueProjectBomDuplicateType.default;
  if (isDefault && !selectedTargetProjectUuid.value) {
    return;
  }
  emit(
    'confirm',
    isDefault ? selectedTargetProjectUuid.value : null,
    branchType.value,
  );
};

watch(branchType, async (val) => {
  const isDefault = val === CatalogueProjectBomDuplicateType.default;
  showProjectSelect.value = isDefault;
  if (isDefault) {
    selectedTargetProjectUuid.value = null;
    await ensureProjects();
  } else {
    selectedTargetProjectUuid.value = null;
  }
});
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    @update:model-value="(v) => emit('update:modelValue', v)"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon icon="mdi-content-copy" class="mr-2" />
        {{ title || $gettext('Duplicate BOM to Project') }}
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="close" />
      </v-card-title>
      <v-card-text>
        <v-select
          v-model="branchType"
          :items="[
            {
              title: $gettext('Duplicate (no relation)'),
              value: CatalogueProjectBomDuplicateType.default,
            },
            {
              title: $gettext('Create Branch'),
              value: CatalogueProjectBomDuplicateType.branch,
            },
            {
              title: $gettext('Create Version'),
              value: CatalogueProjectBomDuplicateType.version,
            },
          ]"
          item-title="title"
          item-value="value"
          :label="$gettext('Descendant Type')"
          class="mb-4"
        />
        <v-select
          v-if="showProjectSelect"
          v-model="selectedTargetProjectUuid"
          :items="projects"
          item-title="name"
          item-value="uuid"
          :label="$gettext('Target Project')"
          :loading="loading"
          clearable
          required
        />
        <div class="text-medium-emphasis text-center text-caption mt-2">
          <div>
            {{
              $gettext(
                'Note: Branch and Version can only target the same project as the source BOM.',
              )
            }}
          </div>
        </div>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="text" @click="close">{{ $gettext('Cancel') }}</v-btn>
        <v-btn
          color="primary"
          :disabled="showProjectSelect && !selectedTargetProjectUuid"
          @click="onConfirm"
        >
          {{ $gettext('Create') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped></style>
